import type { HTMLComponent } from "@commercekit/react";
import { useMemo, type FC } from "react";
import {
  useLocale,
  useLocalePath,
  useWovarProductMarketingSticker,
} from "@commercekit/remix/index";
import type { WovarCustomer, WovarProduct } from "@commercekit/api";
import WvCategoryActionBar from "./WvCategoryActionBar";
import { usePrismicLabels } from "~/hooks/usePrismicLabel";
import { optimizeImageUrl } from "~/lib/imageUtils";

import {
  fallbackMoney,
  formatMoneyWithCurrency,
  taxExemptedCustomer,
} from "@commercekit/shared";
import classNames from "classnames";
import WvLink from "../atoms/WvLink";
import { useWvSelectItem } from "~/hooks/useDataLayer";
import { useYotpo } from "~/hooks/useYotpo";
import { getDeliveryEstimate } from "~/lib/deliveryEstimate";

// Helper types for price components
type PriceData = {
  value: number;
  currencyCode: string;
};

type PriceDisplayProps = {
  localeFullName: string;
  price: PriceData | null | undefined;
  className?: string;
  dataPrice?: number;
};

type TaxInfoProps = {
  customer?: WovarCustomer;
  notB2B: boolean;
  localeFullName: string;
  priceExclTax: PriceData | null | undefined;
  showOnlyPriceExclVat: boolean;
  $pt: (key: string) => string;
};

type VariantPriceDisplayProps = {
  variant: WovarProduct['variants'][0];
  localeFullName: string;
  notB2B: boolean;
  showOnlyPriceExclVat: boolean;
  customer?: WovarCustomer;
  $pt: (key: string) => string;
};

type ProductPriceDisplayProps = {
  product: WovarProduct;
  localeFullName: string;
  notB2B: boolean;
  showOnlyPriceExclVat: boolean;
  customer?: WovarCustomer;
  $pt: (key: string) => string;
};

// Helper component for displaying individual price spans
const PriceItem: FC<PriceDisplayProps> = ({ 
  localeFullName, 
  price, 
  className = "", 
  dataPrice 
}) => {
  const formattedPrice = formatMoneyWithCurrency(
    localeFullName,
    price?.currencyCode ? price : fallbackMoney()
  );
  
  const isBigPrice = formattedPrice?.length >= 10;
  
  return (
    <span
      className={classNames(className, {
        "product-price-big": isBigPrice,
      })}
      data-price={dataPrice}
    >
      {formattedPrice}
    </span>
  );
};

// Helper component for tax information
const TaxInfo: FC<TaxInfoProps> = ({ 
  customer, 
  notB2B, 
  localeFullName, 
  priceExclTax, 
  showOnlyPriceExclVat,
  $pt 
}) => {
  if (taxExemptedCustomer(customer?.type)) {
    return (
      <span className="ex-btw mt-1">
        {$pt("product_label_tax_exempted_price")}
      </span>
    );
  }

  return (
    <span className="ex-btw mt-1">
      <span
        className={classNames("mr-1", showOnlyPriceExclVat ? "only-price-excl-vat" : "d-inline")}
        data-price={priceExclTax?.value}
      >
        {notB2B
          ? formatMoneyWithCurrency(
              localeFullName,
              priceExclTax?.currencyCode ? priceExclTax : fallbackMoney()
            )
          : null}
      </span>
      {$pt("product_label_excluding_tax")}
    </span>
  );
};

// Helper component for variant price display
const VariantPriceDisplay: FC<VariantPriceDisplayProps> = ({
  variant,
  localeFullName,
  notB2B,
  showOnlyPriceExclVat,
  customer,
  $pt,
}) => {
  const hasSalePrice = variant?.salePriceExclTax && variant.salePriceExclTax.value > 0;
  
  if (hasSalePrice) {
    return (
      <>
        {!showOnlyPriceExclVat && (
          <>
            {notB2B ? (
              <>
                <PriceItem
                  localeFullName={localeFullName}
                  price={variant.priceInclTax}
                  className="price-old"
                  dataPrice={variant.priceInclTax?.value}
                />
                <PriceItem
                  localeFullName={localeFullName}
                  price={variant.salePriceInclTax}
                  className="price"
                  dataPrice={variant.salePriceInclTax?.value}
                />
              </>
            ) : (
              <>
                <PriceItem
                  localeFullName={localeFullName}
                  price={variant.priceExclTax}
                  className="price-old"
                  dataPrice={variant.priceExclTax?.value}
                />
                <PriceItem
                  localeFullName={localeFullName}
                  price={variant.salePriceExclTax}
                  className="price"
                  dataPrice={variant.salePriceExclTax?.value}
                />
              </>
            )}
          </>
        )}
        <TaxInfo
          customer={customer}
          notB2B={notB2B}
          localeFullName={localeFullName}
          priceExclTax={variant.salePriceExclTax}
          showOnlyPriceExclVat={showOnlyPriceExclVat}
          $pt={$pt}
        />
      </>
    );
  }

  return (
    <>
      {!showOnlyPriceExclVat && (
        <>
          {notB2B ? (
            <PriceItem
              localeFullName={localeFullName}
              price={variant.priceInclTax}
              className="price"
              dataPrice={variant.priceInclTax?.value}
            />
          ) : (
            <PriceItem
              localeFullName={localeFullName}
              price={variant.priceExclTax}
              className="price"
              dataPrice={variant.priceExclTax?.value}
            />
          )}
        </>
      )}
      <TaxInfo
        customer={customer}
        notB2B={notB2B}
        localeFullName={localeFullName}
        priceExclTax={variant.priceExclTax}
        showOnlyPriceExclVat={showOnlyPriceExclVat}
        $pt={$pt}
      />
    </>
  );
};

// Helper component for product price display (non-variant)
const ProductPriceDisplay: FC<ProductPriceDisplayProps> = ({
  product,
  localeFullName,
  notB2B,
  showOnlyPriceExclVat,
  customer,
  $pt,
}) => {
  const hasSalePrice = product?.salePriceInclTax && product.salePriceInclTax.value > 0;
  
  if (hasSalePrice) {
    return (
      <>
        {!showOnlyPriceExclVat && (
          <>
            {notB2B ? (
              <>
                <PriceItem
                  localeFullName={localeFullName}
                  price={product.priceInclTax}
                  className="price-old"
                  dataPrice={product.priceInclTax?.value}
                />
                <PriceItem
                  localeFullName={localeFullName}
                  price={product.salePriceInclTax}
                  className="price"
                  dataPrice={product.salePriceInclTax?.value}
                />
              </>
            ) : (
              <>
                <PriceItem
                  localeFullName={localeFullName}
                  price={product.priceExclTax}
                  className="price-old"
                  dataPrice={product.priceExclTax?.value}
                />
                <PriceItem
                  localeFullName={localeFullName}
                  price={product.salePriceExclTax}
                  className="price"
                  dataPrice={product.salePriceExclTax?.value}
                />
              </>
            )}
          </>
        )}
        <TaxInfo
          customer={customer}
          notB2B={notB2B}
          localeFullName={localeFullName}
          priceExclTax={product.salePriceExclTax}
          showOnlyPriceExclVat={showOnlyPriceExclVat}
          $pt={$pt}
        />
      </>
    );
  }

  return (
    <>
      {!showOnlyPriceExclVat && (
        <>
          {notB2B ? (
            <PriceItem
              localeFullName={localeFullName}
              price={product.priceInclTax}
              className="price"
              dataPrice={product.priceInclTax?.value}
            />
          ) : (
            <PriceItem
              localeFullName={localeFullName}
              price={product.priceExclTax}
              className="price"
              dataPrice={product.priceExclTax?.value}
            />
          )}
        </>
      )}
      <TaxInfo
        customer={customer}
        notB2B={notB2B}
        localeFullName={localeFullName}
        priceExclTax={product.priceExclTax}
        showOnlyPriceExclVat={showOnlyPriceExclVat}
        $pt={$pt}
      />
    </>
  );
};

const WvProductItem: HTMLComponent<{
  product: WovarProduct;
  className?: string;
  showActionButton?: boolean;
  itemListId: string;
  itemListName: string;
  customer?: WovarCustomer;
  outOfStock?: boolean;
  showOnlyPriceExclVat?: boolean;
  imagePriority?: "eager" | "high";
  largeImage?: boolean;
}> = ({
  showActionButton = true,
  product,
  itemListId,
  itemListName,
  className = "",
  customer,
  outOfStock = false,
  showOnlyPriceExclVat = false,
  imagePriority,
  largeImage = false,
  ...props
}) => {
  const { localeFullName } = useLocale();
  const { $pt } = usePrismicLabels();
  const localePath = useLocalePath();
  const yotpo = useYotpo();
  const { stickerTitle } = useWovarProductMarketingSticker(product);

  const StickerLabel: FC<{ title: string }> = ({ title }) =>
    title ? (
      <div className="productblock__toplabel" data-icon-before="">
        {title}
      </div>
    ) : null;

  const gtmSelectItem = useWvSelectItem();

  const notB2B = useMemo(
    () => !customer || customer.type === "B2C",
    [customer]
  );

  const hasSalePrice = product?.salePriceInclTax && product.salePriceInclTax.value > 0;
  const isBigPrice = formatMoneyWithCurrency(
    localeFullName,
    product?.priceInclTax?.currencyCode
      ? product.priceInclTax
      : fallbackMoney()
  )?.length >= 10

  return (
    <div
      className={`${
        className
          ? className
          : "col-12 col-sm-6 col-lg-12 col-xl-6 mb-3 mb-sm-4"
      }`}
    >
      <div
        className={classNames("productblock h-100 text-left", {
          "productblock--large-image": largeImage,
        })}
      >
        <StickerLabel title={stickerTitle} />
        <WvLink
          to={localePath(`/${product.slug}`)}
          onClick={() => gtmSelectItem(product, itemListId, itemListName)}
          className="d-flex flex-column justify-content-between pt-4 pb-2 h-100"
        >
          <div className="row">
            <div
              className={classNames("col-12", {
                "col-md-8 order-md-1": !largeImage,
              })}
            >
              <span className="productblock__title heading sans-serif strong d-block mb-2 mb-md-1">
                <div dangerouslySetInnerHTML={{ __html: product.name }}></div>
              </span>
              <div className="d-flex d-md-none gap-2 align-items-center">
                <span className="d-md-none">
                  {product?.productUnit ? (
                    <span className="productblock__label d-inline-block">
                      {product?.productUnit}
                    </span>
                  ) : null}
                </span>
                <div
                  className="yotpo-widget-instance d-md-none mb-md-0"
                  style={{ minHeight: "16px" }}
                  data-yotpo-instance-id={yotpo?.starRating}
                  data-yotpo-product-id={product?.sku}
                ></div>
              </div>
              <span className="d-none d-md-flex">
                {product.productUnit ? (
                  <span className="productblock__label d-inline-block">
                    {product.productUnit}
                  </span>
                ) : null}
                <div
                  className="yotpo-widget-instance ml-2 d-flex align-items-center"
                  data-yotpo-instance-id={yotpo?.starRating}
                  data-yotpo-product-id={product?.sku}
                ></div>
              </span>
            </div>
            <div
              className={classNames(
                "col-12 d-flex justify-content-md-center align-items-center",
                { "col-md-4": !largeImage }
              )}
            >
              <picture className="productblock__image img">
                {largeImage && product?.images[0]?.alternativeSizes?.[2]?.url ? (
                  <source
                    media="(min-width: 768px)"
                    srcSet={optimizeImageUrl(
                      product.images[0].alternativeSizes[2].url
                    )}
                  />
                ) : null}
                <img
                  src={optimizeImageUrl(
                    product?.images[0]?.alternativeSizes?.[1]?.url ||
                    product.defaultImage?.urlThumbnail ||
                    product?.images[0]?.urlThumbnail ||
                    ""
                  )}
                  loading={imagePriority ? undefined : "lazy"}
                  fetchPriority={imagePriority === "high" ? "high" : undefined}
                  height="auto"
                  width="100"
                  alt={decodeURIComponent(
                    product?.defaultImage?.altText || ""
                  )}
                />
              </picture>
            </div>
          </div>
          <div className="productblock__specs mb-2 d-none d-md-block">
            <ul>
              {product.advantages
                ? product.advantages?.map((advantage, index) =>
                    advantage.value.stringValue ? (
                      <li
                        key={`${advantage.name}_${index}`}
                        data-icon-before=""
                        className="pb-2 pb-md-1 pl-4"
                      >
                        <div
                          dangerouslySetInnerHTML={{
                            __html: advantage?.value?.stringValue,
                          }}
                        ></div>
                      </li>
                    ) : null
                  )
                : null}
            </ul>
          </div>
          <div className="productblock__stock-delivery">
            {outOfStock ? (
              <span className="not-in-stock">
                {$pt("product_label_out_of_stock")}
              </span>
            ) : (
              product?.unitsOfMeasure[0]?.availability?.canAddToCart ===
                true && (
                <>
                  {product.variants && product.variants.length > 0 ? (
                    <span className="in-stock" data-icon-before="">
                      {$pt("product_label_variant_delivery_est")}
                    </span>
                  ) : (
                    <span data-icon-before="">
                      {getDeliveryEstimate({
                        $pt,
                        deliveryEstimate:
                          product?.unitsOfMeasure[0]?.availability
                            ?.deliveryEstimateMessage,
                        deliveryDate:
                          product?.unitsOfMeasure[0]?.availability
                            ?.deliveryDate,
                        workingDays:
                          product?.unitsOfMeasure[0]?.availability
                            ?.workingDays,
                        defaultShipping:
                          product?.unitsOfMeasure[0]?.defaultShipping,
                      })}
                    </span>
                  )}
                </>
              )
            )}
          </div>
          {!product?.variants || !product?.variants?.length ? (
            <div className="row no-gutters productblock__cta">
              {showActionButton &&
              product?.unitsOfMeasure[0]?.availability?.canAddToCart ? (
                <WvCategoryActionBar product={product} />
              ) : null}
            </div>
          ) : null}

          <span
            className={classNames("productblock__price", {
              productblock__price_big:
                formatMoneyWithCurrency(
                  localeFullName,
                  product?.priceInclTax?.currencyCode
                    ? product.priceInclTax
                    : fallbackMoney()
                )?.length >= 10,
            })}
          >
            <div className="hexagon-product hexagon-primary">
              <div className="hexagon-inner">
                <div className="hexagon-content">
                  {product?.variants && product?.variants?.length > 0 ? (
                    <div
                      className={classNames("product-price-inner", {
                        from:
                          product?.salePriceInclTax &&
                          product?.salePriceInclTax?.value > 0,
                        "product-price-inner-big": isBigPrice,
                        "no-sale-price": isBigPrice && !hasSalePrice
                      })}
                    >
                      <span className="price-from">
                        {$pt("product_label_variant_from")}
                      </span>
                      <VariantPriceDisplay
                        variant={product.variants[0]}
                        localeFullName={localeFullName}
                        notB2B={notB2B}
                        showOnlyPriceExclVat={showOnlyPriceExclVat}
                        customer={customer}
                        $pt={$pt}
                      />
                    </div>
                  ) : null}
                  {!product?.variants || !product?.variants?.length ? (
                    <div
                      className={classNames("product-price-inner", {
                        sale:
                          product?.salePriceInclTax &&
                          product?.salePriceInclTax?.value > 0,
                        "product-price-inner-big": isBigPrice,
                        "no-sale-price": isBigPrice && !hasSalePrice,
                      })}
                    >
                      <ProductPriceDisplay
                        product={product}
                        localeFullName={localeFullName}
                        notB2B={notB2B}
                        showOnlyPriceExclVat={showOnlyPriceExclVat}
                        customer={customer}
                        $pt={$pt}
                      />
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          </span>
        </WvLink>
      </div>
    </div>
  );
};

export default WvProductItem;
